import { type ReactNode, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Vendor } from '@/models/insuranceTypes'
import type { QuoteAnalyticsSummary, VendorRow } from '@/models/quoteAnalytics'
import { BreakdownBarChart, type BreakdownMode } from './BreakdownBarChart'
import { CarrierDailyChart } from './CarrierDailyChart'

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardHeader className="pb-1">
        <CardTitle className="text-sm font-medium text-neutral-500">{label}</CardTitle>
      </CardHeader>
      <CardContent className="text-2xl font-semibold">{value}</CardContent>
    </Card>
  )
}

/** Marks a Rocklake bar only when the same carrier also has a bar under another vendor. */
function vendorRowLabel(row: VendorRow, rows: VendorRow[]): string {
  if (row.vendor !== Vendor.ROCKLAKE) return row.carrier
  const seenElsewhere = rows.some((other) => other.carrier === row.carrier && other.vendor !== row.vendor)
  return seenElsewhere ? `${row.carrier} (Rocklake)` : row.carrier
}

const usd = (n: number) => `$${Math.round(n).toLocaleString()}`
const pct = (n: number) => `${(n * 100).toFixed(1)}%`

export function OverviewTab({ summary }: { summary: QuoteAnalyticsSummary }) {
  const k = summary.kpis
  const [mode, setMode] = useState<BreakdownMode>('counts')
  return (
    <div className="flex flex-col gap-6 pt-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
        <Kpi label="Total" value={String(k.total)} />
        <Kpi label="Generated" value={String(k.generated)} />
        <Kpi label="Declined" value={String(k.declined)} />
        <Kpi label="Unavailable" value={String(k.unavailable)} />
        <Kpi label="Error" value={String(k.errored)} />
        <Kpi label="Decline rate" value={pct(k.declineRate)} />
        <Kpi label="Total premium" value={usd(k.totalPremium)} />
      </div>

      <ChartCard title="Daily outcomes by carrier">
        <CarrierDailyChart series={summary.byVendorDay} />
      </ChartCard>

      <div className="flex gap-2">
        <Button variant={mode === 'counts' ? 'default' : 'outline'} onClick={() => setMode('counts')}>
          Counts
        </Button>
        <Button variant={mode === 'premium' ? 'default' : 'outline'} onClick={() => setMode('premium')}>
          Premium
        </Button>
      </div>

      <ChartCard title="By vendor / carrier">
        <BreakdownBarChart
          data={summary.byVendor.map((r) => ({ ...r, label: vendorRowLabel(r, summary.byVendor) }))}
          xKey="label"
          mode={mode}
        />
      </ChartCard>
      <ChartCard title="By state">
        <BreakdownBarChart data={summary.byState} xKey="state" mode={mode} />
      </ChartCard>
      <ChartCard title="By fleet size">
        <BreakdownBarChart data={summary.byFleet} xKey="bucket" mode={mode} />
      </ChartCard>
      <ChartCard title="By month">
        <BreakdownBarChart data={summary.byMonth} xKey="period" mode={mode} />
      </ChartCard>

      <Card>
        <CardHeader>
          <CardTitle>Decline / unavailable reasons</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Vendor</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead className="text-right">Count</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {summary.declineReasons.map((r, i) => (
                <TableRow key={i}>
                  <TableCell>{r.vendor}</TableCell>
                  <TableCell>{r.reason}</TableCell>
                  <TableCell className="text-right">{r.count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div>
        <h2 className="mb-3 text-lg font-semibold">Premium distribution (per unit)</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>By state</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>State</TableHead>
                    <TableHead className="text-right">Avg per unit</TableHead>
                    <TableHead className="text-right">Median per unit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summary.premiumDistribution.byState.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-neutral-400">
                        No data
                      </TableCell>
                    </TableRow>
                  ) : (
                    summary.premiumDistribution.byState.map((r) => (
                      <TableRow key={r.state}>
                        <TableCell>{r.state}</TableCell>
                        <TableCell className="text-right">{usd(r.avgPerUnit)}</TableCell>
                        <TableCell className="text-right">{usd(r.medianPerUnit)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>By fleet size</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fleet</TableHead>
                    <TableHead className="text-right">Avg per unit</TableHead>
                    <TableHead className="text-right">Median per unit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summary.premiumDistribution.byFleet.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-neutral-400">
                        No data
                      </TableCell>
                    </TableRow>
                  ) : (
                    summary.premiumDistribution.byFleet.map((r) => (
                      <TableRow key={r.bucket}>
                        <TableCell>{r.bucket}</TableCell>
                        <TableCell className="text-right">{usd(r.avgPerUnit)}</TableCell>
                        <TableCell className="text-right">{usd(r.medianPerUnit)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

function ChartCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  )
}
